<template>
  <div class="warehouse-placeholder">
    <div class="warehouse-placeholder__picker">
      <grocery-picker
        ref="groceryPicker"
        v-model="selectedGrocery"
        :groceries="availableGroceries"
        :placeholder="getHint"
        :loading="loading"
        hide-details
        dense
        persistent-hint
        @enter="addGrocery"
        :disable-add="!['purchase', 'entry'].includes(type)"
      />
    </div>
    <div class="warehouse-placeholder__actions">
      <v-btn
        small
        color="success"
        depressed
        class="warehouse-placeholder__button"
        :disabled="!canAdd"
        @mousedown.prevent="capturePendingGrocery"
        @click="handleAddButtonClick"
      >
        <v-icon small left>mdi-plus</v-icon>
        {{ $t("add") }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import hasSnackbar from "@/mixins/hasSnackbar";
import warehouseService from "@/services/warehouse";
import GroceryPicker from "@/components/groceries/fields/GroceryPicker.vue";

export default {
  name: "WarehouseGroceryPlaceholder",
  mixins: [hasSnackbar],
  components: {
    GroceryPicker,
  },
  props: {
    warehouse: {
      type: [Object, null, undefined],
      required: true,
    },
    type: {
      type: String,
      required: true,
      validator: (value) =>
        ["waybill", "purchase", "entry", "writeoff", "return"].includes(value),
    },
    existingGroceries: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    selectedGrocery: null,
    pendingGrocery: null,
    loading: false,
    inventory: [],
    inventoryLoaded: false,
    groceries: [],
    groceriesLoaded: false,
  }),
  computed: {
    availableGroceries() {
      // Za purchase i entry koristimo groceries, za ostale inventory
      const isPurchaseOrEntry = ["purchase", "entry"].includes(this.type);

      if (isPurchaseOrEntry) {
        if (!this.groceriesLoaded) return [];

        // Filtriraj artikle koji su već dodati u tabelu
        const existingGroceryIds = this.existingGroceries.map(
          (g) => g.grocery_id || g.id
        );

        return this.groceries
          .filter((item) => !existingGroceryIds.includes(item.id))
          .map((item) => ({
            ...item,
            grocery_id: item.id,
            grocery: item,
            quantity: 0,
          }));
      } else {
        if (!this.inventoryLoaded) return [];

        let filtered = this.inventory.filter(
          (item) => item.warehouse_id === this.warehouse?.id
        );
        // Za waybill, writeoff i return - prikazuj samo namirnice sa zalihama
        if (["waybill", "writeoff", "return"].includes(this.type)) {
          filtered = filtered.filter((item) => item.quantity > 0 || item.total > 0);
        }
        // Filtriraj artikle koji su već dodati u tabelu
        const existingGroceryIds = this.existingGroceries.map(
          (g) => g.grocery_id || g.id
        );
        filtered = filtered.filter(
          (item) => !existingGroceryIds.includes(item.grocery_id)
        );

        return filtered.map((item) => ({
          ...item,
          ...item.grocery,
        }));
      }
    },
    canAdd() {
      return Boolean(this.selectedGrocery || this.pendingGrocery);
    },
    getHint() {
      if (this.loading) {
        return this.$t("loading_inventory_please_wait");
      }
      if (["waybill", "writeoff", "return"].includes(this.type)) {
        return (
          "← " +
          this.$t("warehouse.quick_add_hint") +
          " | " +
          this.$t("only_available_items_shown")
        );
      }
      return "← " + this.$t("warehouse.quick_add_hint");
    },
  },
  methods: {
    capturePendingGrocery() {
      this.pendingGrocery =
        this.selectedGrocery || this.$refs.groceryPicker?.getPendingGrocery?.() || null;
    },
    handleAddButtonClick() {
      const groceryToAdd =
        this.pendingGrocery ||
        this.selectedGrocery ||
        this.$refs.groceryPicker?.getPendingGrocery?.() ||
        null;

      this.addGrocery(groceryToAdd);
      this.pendingGrocery = null;
    },
    async getInventory() {
      this.loading = true;
      try {
        const { data } = await warehouseService.getInventory();
        this.inventory = data;
        this.inventoryLoaded = true;
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_inventory"));
      } finally {
        this.loading = false;
      }
    },
    async getGroceries() {
      this.loading = true;
      try {
        const groceryService = await import("@/services/grocery");
        const { data } = await groceryService.default.getGroceries();
        this.groceries = data;
        this.groceriesLoaded = true;
      } catch (error) {
        this.showErrorSnackbar(error, this.$t("error_loading_groceries"));
      } finally {
        this.loading = false;
      }
    },
    addGrocery(enteredGrocery = null) {
      const selectedGrocery = enteredGrocery || this.selectedGrocery;

      if (!selectedGrocery) return;

      if (enteredGrocery) {
        this.selectedGrocery = enteredGrocery;
      }

      const isPurchaseOrEntry = ["purchase", "entry"].includes(this.type);

      const grocery = {
        id: isPurchaseOrEntry ? selectedGrocery.id : selectedGrocery.grocery_id,
        grocery_id: isPurchaseOrEntry
          ? selectedGrocery.id
          : selectedGrocery.grocery.id,
        code: isPurchaseOrEntry
          ? selectedGrocery.code
          : selectedGrocery.grocery.code,
        name: isPurchaseOrEntry
          ? selectedGrocery.name
          : selectedGrocery.grocery.name,
        measure: selectedGrocery.measure,
        quantity: isPurchaseOrEntry ? 1 : selectedGrocery.quantity || 0,
        warehouse_stock: isPurchaseOrEntry ? 0 : selectedGrocery.quantity || 0,
        warehouse_id: this.warehouse.id,
        grocery: isPurchaseOrEntry ? selectedGrocery : selectedGrocery.grocery,
        warehouse: this.warehouse,
        transaction_type: this.type === "purchase" ? "order" : this.type,
      };

      this.$emit("groceryAdded", grocery);
      this.resetForm();
    },
    resetForm() {
      this.selectedGrocery = null;
      this.pendingGrocery = null;
    },
  },
  watch: {
    warehouse: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (!newVal) return;

        const isPurchaseOrEntry = ["purchase", "entry"].includes(this.type);

        if (isPurchaseOrEntry && !this.groceriesLoaded) {
          this.getGroceries();
        } else if (!isPurchaseOrEntry && !this.inventoryLoaded) {
          this.getInventory();
        }
      },
    },
    type: {
      immediate: true,
      handler(newVal) {
        if (!this.warehouse) return;

        const isPurchaseOrEntry = ["purchase", "entry"].includes(newVal);

        if (isPurchaseOrEntry && !this.groceriesLoaded) {
          this.getGroceries();
        } else if (!isPurchaseOrEntry && !this.inventoryLoaded) {
          this.getInventory();
        }
      },
    },
  },
};
</script>

<style scoped>
.warehouse-placeholder {
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 16px;
  background-color: #fff;
}

.warehouse-placeholder:hover {
  background-color: #eeeeee;
}

.warehouse-placeholder__picker {
  flex: 1 1 auto;
  min-width: 0;
}

.warehouse-placeholder__actions {
  flex: 0 0 auto;
  display: flex;
  justify-content: flex-end;
}

.warehouse-placeholder__button {
  height: 40px !important;
}

@media (max-width: 600px) {
  .warehouse-placeholder {
    flex-direction: column;
  }

  .warehouse-placeholder__actions {
    width: 100%;
  }
}
</style>
